/**
 * Types + fetchery dla pluginu `c_advertisements` rozszerzającego shell
 * `c_user_accounts` (zarządzanie własnymi ogłoszeniami przez zalogowanego usera).
 *
 * Backend (IRESTAPI, routing po nazwie metody - `IAPI_` strip):
 *   - GET    /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdverts?page=&per_page=&public=
 *   - GET    /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdvert/{id}
 *   - GET    /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdvertOptions
 *   - POST   /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdvertSave
 *   - PUT    /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdvertUpdate/{id}
 *   - DELETE /IRESTAPI/v1/C_ADVERTISEMENTS_CONTROLLER/userAdvertDelete/{id}
 *
 * Frontend route handlery (proxy + Bearer) w app/api/iiapi/c-advertisements/.
 */

export type AdvertCover = {
  name: string;
  thumb_src: string;
  width: number | null;
  height: number | null;
};

export type UserAdvert = {
  id: number;
  name: string;
  category_id: number;
  category_name: string;
  add_date: string | null;
  availability: string | null;
  availability_expired: boolean;
  price: number | null;
  negotiated_price: boolean;
  /** 0 = niepublikowane, 1 = publikowane. */
  public: number;
  /** Miniatura okładki (zdjęcia z `is_main=1` lub pierwsze po sort). `null`
   *  gdy ogłoszenie nie ma zdjęć. */
  cover: AdvertCover | null;
};

export type UserAdvertDetail = UserAdvert & {
  type_id: number;
  text: string;
  short_text: string;
};

export type AdvertOption = {
  id: number;
  name: string;
  /** ID katalogu c_advertisements do którego należy ten element
   *  (kategoria -> jej id_parent; typ -> jego id_catalog). Frontend filtruje
   *  typy per wybrana kategoria po dopasowaniu `parent_id`. */
  parent_id: number;
};

export type AdvertPagination = {
  page: number;
  per_page: number;
  total: number;
  total_pages: number;
};

export type AdvertFormPayload = {
  name: string;
  text: string;
  category_id: number;
  type_id: number;
  availability: string; // Y-m-d
  negotiated_price: boolean;
  price: number | null;
  public: boolean;
};

type Envelope<T> = ({ ok: true } & T) | { ok: false; error?: string; message?: string; code?: number | string };

async function readJson<T>(res: Response, fallbackMsg: string): Promise<T> {
  const body = (await res.json()) as Envelope<{ data: T }>;
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    const msg = "message" in body && body.message ? body.message : `${fallbackMsg} (HTTP ${res.status})`;
    throw new Error(msg);
  }
  return body.data;
}

/** Lista ogłoszeń usera (paginowana). */
export async function fetchUserAdverts(
  page = 1,
  perPage = 20,
): Promise<{ adverts: UserAdvert[]; pagination: AdvertPagination }> {
  const res = await fetch(`/api/iiapi/c-advertisements/adverts?page=${page}&per_page=${perPage}`, {
    cache: "no-store",
  });
  return readJson(res, "Nie udało się pobrać ogłoszeń");
}

/** Pojedyncze ogłoszenie (meta + opis) - do formularza edycji. */
export async function fetchUserAdvert(id: number): Promise<{ advert: UserAdvertDetail }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${id}`, { cache: "no-store" });
  return readJson(res, "Nie udało się pobrać ogłoszenia");
}

/** Opcje formularza - lista kategorii (podkatalogów) + lista typów. */
export async function fetchAdvertOptions(): Promise<{
  categories: AdvertOption[];
  types: AdvertOption[];
}> {
  const res = await fetch(`/api/iiapi/c-advertisements/options`, { cache: "no-store" });
  return readJson(res, "Nie udało się pobrać opcji formularza");
}

/** Zgłoszenie propozycji nowej kategorii ogłoszeń. Backend wysyła powiadomienie
 *  systemowe (bez maila) do grupy `system_administrator` - żadne dane nie
 *  trafiają do tabeli. Administrator moderuje przez panel powiadomień. */
export async function suggestAdvertCategory(payload: {
  name: string;
  description?: string;
}): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-advertisements/category-suggestion`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readJson(res, "Nie udało się wysłać propozycji kategorii");
}

/** Utworzenie ogłoszenia. Zwraca id nowego ogłoszenia. */
export async function createUserAdvert(payload: AdvertFormPayload): Promise<{ advert_id: number }> {
  const res = await fetch(`/api/iiapi/c-advertisements/adverts`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readJson(res, "Nie udało się utworzyć ogłoszenia");
}

/** Aktualizacja ogłoszenia. */
export async function updateUserAdvert(id: number, payload: AdvertFormPayload): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    cache: "no-store",
  });
  return readJson(res, "Nie udało się zapisać ogłoszenia");
}

/** Usunięcie ogłoszenia (wraz z plikami zdjęć). */
export async function deleteUserAdvert(id: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${id}`, {
    method: "DELETE",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się usunąć ogłoszenia");
}

/* Zdjęcia ogłoszenia */

export type UserAdvertImage = {
  id: number;
  name: string;
  description: string;
  is_main: boolean;
  thumb_src: string;
  full_src: string;
  width: number | null;
  height: number | null;
};

/** Lista zdjęć dla ogłoszenia. */
export async function fetchUserAdvertImages(advertId: number): Promise<{ images: UserAdvertImage[] }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${advertId}/images`, { cache: "no-store" });
  return readJson(res, "Nie udało się pobrać zdjęć");
}

/** Upload zdjęć (multipart). `files` z input[type=file] multiple. */
export async function uploadUserAdvertImages(
  advertId: number,
  files: FileList | File[],
): Promise<{ uploaded: number; errors: string[] }> {
  const fd = new FormData();
  for (const f of Array.from(files)) fd.append("images[]", f);

  const res = await fetch(`/api/iiapi/c-advertisements/advert/${advertId}/images`, {
    method: "POST",
    body: fd,
    cache: "no-store",
  });
  return readJson(res, "Nie udało się wgrać zdjęć");
}

/** Usunięcie pojedynczego zdjęcia. */
export async function deleteUserAdvertImage(advertId: number, imageId: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${advertId}/image/${imageId}`, {
    method: "DELETE",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się usunąć zdjęcia");
}

/** Ustawienie zdjęcia głównego (cover ogłoszenia). */
export async function setUserAdvertMainImage(advertId: number, imageId: number): Promise<{ success: boolean }> {
  const res = await fetch(`/api/iiapi/c-advertisements/advert/${advertId}/image/${imageId}/main`, {
    method: "POST",
    cache: "no-store",
  });
  return readJson(res, "Nie udało się ustawić zdjęcia głównego");
}
